import { useQuery, useMutation } from '@tanstack/react-query'
import Card from 'react-bootstrap/Card'
import { fetchUserInterests, syncInterests, syncProgress } from './data'
import { RandomColorBox } from '@/component/RandomColorBox'
import { HeartIcon } from 'lucide-react'
import { SquarePen } from 'lucide-react'
import { useEffect, useState } from 'react'
import { queryClient } from '@/util/queryClient'
import { useSelectable } from '@/hook'
import type { InterestCategory, Interest } from '@/util/ref'
import { fetchInterests } from '@/util/ref'
import { ProgressFlags } from '@/util/progress'
import { cursorPointer, active, cardStyle } from './style.css'
import Container from 'react-bootstrap/esm/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import clsx from 'clsx'
import type { JSX } from 'react'
import Button from 'react-bootstrap/esm/Button'
import Stack from 'react-bootstrap/esm/Stack'

export default function InterestsPanel() {
  const { data: interestCategories } = useQuery<InterestCategory[]>({
    queryKey: ['interest'],
    queryFn: fetchInterests,
    staleTime: 1000 * 60 * 5,
  })

  const {
    data: userInterests,
    // isLoading: interestsLoading,
    // isError: interestsError,
  } = useQuery<Interest[]>({
    queryKey: ['profile', 'interest'],
    queryFn: fetchUserInterests,
    staleTime: 1000 * 60 * 60,
  })

  // const [interestIds, setInterestIds] = useAtom(interestIdsAtom)
  const [selectedInterestIds, toggle, reset] = useSelectable<number>([], 5)
  const interestsMutation = useMutation({
    mutationFn: (interestIds: number[]) => syncInterests(interestIds),
    onSuccess: () => {
      // queryClient.setQueryData(['profile', 'interest'], selectedInterestIds)
      queryClient.invalidateQueries({ queryKey: ['profile', 'interest'] })
      syncProgress(ProgressFlags.INTERESTS_SELECTED)
    },
    onError: (err) => console.log(err),
  })

  useEffect(() => {
    reset(userInterests ? userInterests.map(({ id }) => id) : [])
  }, [reset, userInterests])

  const [editMode, setEditMode] = useState<boolean>(false)

  return (
    <Card>
      <Card.Body>
        <Card.Title className="d-flex flex-row justify-content-between align-items-center">
          <div>
            <HeartIcon type="heart" className="me-1" />
            興味分野
          </div>
          {!editMode && <SquarePen onClick={() => setEditMode(true)} className={cursorPointer} />}
        </Card.Title>
        {editMode ? (
          <>
            <div>
              <p className="text-muted small">最大5つまで選択してください</p>
              <p className="small text-muted">選択済み: {selectedInterestIds.length}/1（最低）</p>
            </div>
            <Container>
              {interestCategories?.reduce<JSX.Element[]>((acc, cur, i, arr) => {
                const next = arr[i + 1]
                if (i % 2 === 0) {
                  acc.push(
                    <Row className="mb-2" key={`row-${cur.id}`}>
                      <Col key={cur.id}>
                        <Card
                          className={clsx(
                            cardStyle,
                            selectedInterestIds.includes(cur.reprId) && active
                          )}
                          onClick={() => toggle(cur.reprId)}
                        >
                          <Card.Body>
                            <b>
                              {cur.emoji} {cur.name}
                            </b>
                            {/*
                          <div className="text-muted">
                            {cur.interests.map((i) => i.name).join(', ')}
                          </div>
                        */}
                          </Card.Body>
                        </Card>
                      </Col>
                      {next && (
                        <Col key={next.id}>
                          <Card
                            className={clsx(
                              cardStyle,
                              selectedInterestIds.includes(next.reprId) && active
                            )}
                            onClick={() => toggle(next.reprId)}
                          >
                            <Card.Body>
                              <b>
                                {next.emoji} {next.name}
                              </b>
                              {/*
                            <div className="text-muted">
                              {next.interests.map((i) => i.name).join(', ')}
                            </div>
                          */}
                            </Card.Body>
                          </Card>
                        </Col>
                      )}
                    </Row>
                  )
                }
                return acc
              }, [])}
              <Stack direction="horizontal" gap={3} className="justify-content-end">
                <Button variant="outline-secondary" onClick={() => setEditMode(false)}>
                  キャンセル
                </Button>
                <Button
                  variant="outline-primary"
                  disabled={selectedInterestIds.length < 1}
                  onClick={() => {
                    interestsMutation.mutate(selectedInterestIds)
                    setEditMode(false)
                  }}
                >
                  保存
                </Button>
              </Stack>
            </Container>
          </>
        ) : userInterests ? (
          userInterests.map(({ id, name, emoji }, i) => (
            <RandomColorBox key={id} index={i}>
              {emoji} {name}
            </RandomColorBox>
          ))
        ) : (
          <Card.Subtitle className="text-muted">興味分野が設定されていません</Card.Subtitle>
        )}
      </Card.Body>
    </Card>
  )
}
